import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API from "../../Api/api";

const CONFIG = {
  checkin: {
    icon: "⏰",
    title: "Final reminder: Check In",
    subtitle: "You were required to check in by 10:00 AM. Check in now, or you will be marked Absent.",
    primary: { label: "Check In", value: "checkin", color: "#0A1628" },
    secondary: { label: "Absent", value: "absent", color: "#DC2626" },
  },
  checkout: {
    icon: "🕗",
    title: "Time to wrap up",
    subtitle: "Check out now, or tap Continue if you are still working.",
    primary: { label: "Check Out", value: "checkout", color: "#0A1628" },
    secondary: { label: "Continue", value: "continue", color: "#06B6D4" },
  },
};

const fmt = (s) => {
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}:${String(s % 60).padStart(2, "0")}` : `${s}s`;
};

// Shows the 10:30 AM (Check In / Absent) and 6:30 PM / 8 PM (Check Out / Continue) alarm popup.
// A web page can't receive background alarms, so this checks with the server while the app is open.
export default function AlarmGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const [alarm, setAlarm] = useState(null); // { type, secondsLeft, message? }
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const closedExpired = useRef(false);

  const load = useCallback(async () => {
    if (!localStorage.getItem("token")) return;
    try {
      const { data } = await API.get("/attendance/alarm/today");
      const ci = data?.data?.checkin;
      const co = data?.data?.checkout;
      setAlarm((cur) => {
        if (cur && cur.message) return cur; // keep a result message until the user closes it
        if (ci && !ci.response && ci.secondsLeft > 0) {
          closedExpired.current = false;
          return { type: "checkin", secondsLeft: ci.secondsLeft };
        }
        if (co && !co.response) return { type: "checkout", secondsLeft: 0 };
        return null;
      });
    } catch (e) {
      /* offline or logged out: ignore */
    }
  }, []);

  // check on every page change, when the tab comes back, and every 20 seconds while visible
  useEffect(() => { load(); }, [load, location.pathname]);
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(() => document.visibilityState === "visible" && load(), 20000);
    return () => { document.removeEventListener("visibilitychange", onVisible); clearInterval(id); };
  }, [load]);

  // countdown for the check-in alarm
  useEffect(() => {
    setLeft(alarm?.secondsLeft || 0);
    if (!alarm || alarm.type !== "checkin" || alarm.message || !alarm.secondsLeft) return;
    const id = setInterval(() => setLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [alarm]);

  const finished = !!alarm && (!!alarm.message || (alarm.type === "checkin" && left === 0));

  // vibrate while the popup is waiting (works on Android browsers; iPhone Safari does not allow it)
  useEffect(() => {
    if (!alarm || finished || !navigator.vibrate) return;
    const buzz = () => navigator.vibrate([700, 300]);
    buzz();
    const id = setInterval(buzz, 1000);
    return () => { clearInterval(id); navigator.vibrate(0); };
  }, [alarm, finished]);

  const answer = async (type, response) => {
    setBusy(true);
    try {
      await API.post("/attendance/alarm/respond", { type, response });
      setAlarm(null);
      if (response === "checkin") navigate("/checkin");
      else if (response === "checkout") navigate("/checkout");
      else if (response === "absent") navigate("/leaves"); // apply leave in time or the day stays Absent
    } catch (e) {
      const d = e?.response?.data;
      if (d?.code === "ALARM_EXPIRED" || d?.code === "ALREADY_ANSWERED") {
        if (response === "checkin") { setAlarm(null); navigate("/checkin"); return; }
        setAlarm({ type, secondsLeft: 0, message: d.message });
      } else {
        window.alert(d?.message || "Could not send. Check your internet connection and try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!alarm) return null;
  const cfg = CONFIG[alarm.type];
  const isCheckIn = alarm.type === "checkin";
  const text = alarm.message || (finished
    ? "Check-in time (10:00 AM) has passed, so today is marked Absent. You can still check in, or ask your admin to change the status."
    : cfg.subtitle);

  const btn = (bg) => ({
    flex: 1, border: "none", borderRadius: 26, padding: "15px 10px", background: bg, color: "#fff",
    fontSize: 15, fontWeight: 700, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.7 : 1,
    fontFamily: "inherit",
  });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ width: "100%", maxWidth: 380, background: "#fff", borderRadius: 28, padding: 24, textAlign: "center", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div style={{ width: 68, height: 68, borderRadius: 34, background: "rgba(249,115,22,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 32 }}>{cfg.icon}</div>
        <div style={{ fontSize: 20, fontWeight: 800, color: "#0F172A", marginTop: 12 }}>{cfg.title}</div>
        <div style={{ fontSize: 13.5, color: "#64748B", marginTop: 8, lineHeight: 1.5 }}>{text}</div>
        {isCheckIn && !finished && (
          <div style={{ display: "inline-block", marginTop: 14, background: "#FFF7ED", borderRadius: 20, padding: "6px 18px", fontSize: 22, fontWeight: 800, color: "#F97316" }}>{fmt(left)}</div>
        )}
        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
          {finished ? (
            <>
              {isCheckIn && <button style={btn("#0A1628")} onClick={() => answer("checkin", "checkin")}>Check In</button>}
              <button style={btn(isCheckIn ? "#64748B" : "#0A1628")} onClick={() => setAlarm(null)}>OK</button>
            </>
          ) : (
            <>
              <button disabled={busy} style={btn(cfg.primary.color)} onClick={() => answer(alarm.type, cfg.primary.value)}>{cfg.primary.label}</button>
              <button disabled={busy} style={btn(cfg.secondary.color)} onClick={() => answer(alarm.type, cfg.secondary.value)}>{cfg.secondary.label}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
